import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Link,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Grid,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { get, getAllFixVersions } from '../../services/apiService';
import StatusBadge from '../common/StatusBadge';
import { formatDate } from '../../utils/formatters';

/**
 * QA Report Tab component for displaying release content in a hierarchical tree view
 * Similar to Release Content view but with tree structure and aggregated test/PR counts
 */
const QAReportTab = ({ fixVersion: propFixVersion }) => {
  const [selectedFixVersion, setSelectedFixVersion] = useState(propFixVersion || '');
  const [fixVersions, setFixVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [orphanBugsExpanded, setOrphanBugsExpanded] = useState(true);
  const [dataTableExpanded, setDataTableExpanded] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [teamOrderBy, setTeamOrderBy] = useState('');
  const [teamOrder, setTeamOrder] = useState('asc');
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [selectedTeamTests, setSelectedTeamTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [selectedTeamName, setSelectedTeamName] = useState('');

  // Filter fix versions to only include those with format 5.9.08 and above
  const isValidFixVersion = (version) => {
    const regex = /^(\d+)\.(\d+)\.(\d+)$/;
    const match = version.match(regex);
    if (!match) return false;
    
    const [, major, minor, patch] = match;
    return (
      parseInt(major) > 5 || 
      (parseInt(major) === 5 && parseInt(minor) > 9) || 
      (parseInt(major) === 5 && parseInt(minor) === 9 && parseInt(patch) >= 8)
    );
  };

  // Sort versions to find the most recent (highest version number)
  const sortVersions = (versions) => {
    return [...versions].sort((a, b) => {
      const regex = /^(\d+)\.(\d+)\.(\d+)$/;
      const matchA = a.match(regex);
      const matchB = b.match(regex);
      
      if (!matchA || !matchB) return 0;
      
      const [, majorA, minorA, patchA] = matchA.map(Number);
      const [, majorB, minorB, patchB] = matchB.map(Number);
      
      if (majorA !== majorB) return majorB - majorA;
      if (minorA !== minorB) return minorB - minorA;
      return patchB - patchA;
    });
  };

  // Fetch fix versions on component mount
  useEffect(() => {
    const fetchFixVersions = async () => {
      try {
        setLoadingVersions(true);
        const response = await getAllFixVersions();
        
        let versionStrings = [];
        
        if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          const activeVersionsData = response.data.data.filter(fixVersion => fixVersion.active);
          versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
        } else if (response && response.data && Array.isArray(response.data)) {
          const activeVersionsData = response.data.filter(fixVersion => fixVersion.active);
          versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
        } else if (Array.isArray(response)) {
          const activeVersionsData = response.filter(fixVersion => fixVersion.active);
          versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
        } else if (response && response.success && Array.isArray(response.data)) {
          const activeVersionsData = response.data.filter(fixVersion => fixVersion.active);
          versionStrings = activeVersionsData.map(fixVersion => fixVersion.version);
        }
        
        const filtered = versionStrings.filter(isValidFixVersion);
        const sorted = sortVersions(filtered);
        setFixVersions(sorted);
        
        // Auto-select the most recent release if none is selected
        if (!selectedFixVersion && sorted.length > 0) {
          setSelectedFixVersion(sorted[0]);
        }
      } catch (err) {
        console.error('Error fetching fix versions:', err);
        setError('Failed to load fix versions. Please try again later.');
      } finally {
        setLoadingVersions(false);
      }
    };
    
    fetchFixVersions();
  }, []);

  // Fetch QA report when selectedFixVersion changes
  useEffect(() => {
    if (!selectedFixVersion) {
      setReportData(null);
      return;
    }

    fetchQAReport();
  }, [selectedFixVersion]);

  // Fetch QA report
  const fetchQAReport = async () => {
    if (!selectedFixVersion) return;

    try {
      setLoading(true);
      setError(null);
      const response = await get(`/api/fix-versions/${selectedFixVersion}/qa-report`);
      
      if (response && response.success && response.data) {
        setReportData(response.data);
        // By default, collapse all items (empty set)
        setExpandedNodes(new Set());
        // Expand orphan bugs section by default if it exists
        if (response.data.orphanBugs && response.data.orphanBugs.length > 0) {
          setOrphanBugsExpanded(true);
        }
        
        // Extract unique teams from the data
        const teamsSet = new Set();
        const extractTeams = (nodes) => {
          nodes.forEach(node => {
            if (node.item.team && node.item.team.trim() !== '') {
              teamsSet.add(node.item.team);
            }
            if (node.children && node.children.length > 0) {
              extractTeams(node.children);
            }
          });
        };
        
        if (response.data.items) {
          extractTeams(response.data.items);
        }
        if (response.data.orphanBugs) {
          extractTeams(response.data.orphanBugs);
        }
        
        const teamsArray = Array.from(teamsSet).sort();
        setAvailableTeams(teamsArray);
      } else {
        setError('Failed to load QA report');
      }
    } catch (err) {
      console.error('Error fetching QA report:', err);
      setError(err.message || 'Failed to load QA report');
    } finally {
      setLoading(false);
    }
  };

  // Refresh functionality removed - data is fetched via "Fetch Latest" button on Release Content tab

  // Toggle node expansion
  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Get sort value for a node
  const getSortValue = (node, property) => {
    switch (property) {
      case 'id':
        return parseInt(node.item.id) || 0;
      case 'parent':
        return (node.item.parent || '').toString().toLowerCase();
      case 'type':
        return (node.item.workItemType || '').toString().toLowerCase();
      case 'title':
        return (node.item.title || '').toString().toLowerCase();
      case 'state':
        return (node.item.state || '').toString().toLowerCase();
      case 'fixVersion':
        return (node.item.fixVersion || '').toString().toLowerCase();
      case 'createdDate':
        return node.item.createdDate ? new Date(node.item.createdDate).getTime() : 0;
      case 'testCount':
        return node.testCount || 0;
      case 'prCount':
        return node.prCount || 0;
      case 'team':
        return (node.item.team || '').toString().toLowerCase();
      default:
        return 0;
    }
  };

  // Sort nodes at a specific level (recursively sorts children too)
  const sortNodes = (nodes) => {
    if (!orderBy || !nodes || nodes.length === 0) return nodes;

    return [...nodes].sort((a, b) => {
      const aValue = getSortValue(a, orderBy);
      const bValue = getSortValue(b, orderBy);

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    }).map(node => {
      // Recursively sort children
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: sortNodes(node.children)
        };
      }
      return node;
    });
  };

  // Filter nodes by team (recursively, maintains hierarchy)
  const filterNodesByTeam = (nodes) => {
    if (!nodes || nodes.length === 0) return [];
    if (selectedTeams.length === 0) return nodes;

    const filtered = [];
    
    nodes.forEach(node => {
      // Check if this node matches the team filter
      const nodeTeam = node.item.team ? node.item.team.trim() : '';
      const nodeMatches = nodeTeam !== '' && selectedTeams.includes(nodeTeam);
      
      // Recursively filter children first
      const filteredChildren = node.children && node.children.length > 0 
        ? filterNodesByTeam(node.children) 
        : [];
      
      // Include node if:
      // 1. Node itself matches the team filter, OR
      // 2. Node has children that match (to show parent even if it doesn't match)
      if (nodeMatches || filteredChildren.length > 0) {
        filtered.push({
          ...node,
          children: filteredChildren
        });
      }
    });
    
    return filtered;
  };

  // Get filtered and sorted data (maintains tree structure, sorts at each level)
  const getSortedData = () => {
    if (!reportData) return { items: [], orphanBugs: [] };

    // First filter by team
    const filteredItems = reportData.items ? filterNodesByTeam(reportData.items) : [];
    const filteredOrphanBugs = reportData.orphanBugs ? filterNodesByTeam(reportData.orphanBugs) : [];

    // Then sort
    return {
      items: filteredItems.length > 0 ? sortNodes(filteredItems) : [],
      orphanBugs: filteredOrphanBugs.length > 0 ? sortNodes(filteredOrphanBugs) : []
    };
  };

  // Flatten tree to get all items for team statistics (includes test counts from nodes)
  const flattenTreeForStats = (nodes) => {
    const items = [];
    nodes.forEach(node => {
      // Include the item with its test count from the node
      items.push({
        ...node.item,
        nodeTestCount: node.testCount || 0
      });
      if (node.children && node.children.length > 0) {
        const childItems = flattenTreeForStats(node.children);
        items.push(...childItems);
      }
    });
    return items;
  };

  // Calculate team statistics
  const calculateTeamStats = () => {
    if (!reportData) return [];

    // Get all items (matching fix version only for stats)
    const allItems = [];
    if (reportData.items) {
      const flattened = flattenTreeForStats(reportData.items);
      allItems.push(...flattened);
    }
    if (reportData.orphanBugs) {
      reportData.orphanBugs.forEach(bug => {
        allItems.push({
          ...bug.item,
          nodeTestCount: bug.testCount || 0
        });
      });
    }

    // Filter by selected teams if any
    const filteredItems = selectedTeams.length > 0
      ? allItems.filter(item => {
          const itemTeam = item.team ? item.team.trim() : '';
          return itemTeam !== '' && selectedTeams.includes(itemTeam);
        })
      : allItems;

    // Group by team
    const teamStats = {};
    // Track unique test IDs per team to avoid double counting
    const teamTestIds = {};
    
    filteredItems.forEach(item => {
      const team = item.team && item.team.trim() !== '' ? item.team.trim() : 'Unassigned';
      
      // Skip tasks - they should not be counted
      if (item.workItemType === 'Task') {
        return;
      }
      
      if (!teamStats[team]) {
        teamStats[team] = {
          team,
          features: 0,
          pbis: 0,
          bugs: 0,
          linkedTests: 0,
          totalItems: 0,
          itemsWithTests: 0
        };
        teamTestIds[team] = new Set();
      }

      const stats = teamStats[team];

      // Count by type (excluding tasks)
      if (item.workItemType === 'Feature') {
        stats.features++;
        stats.totalItems++;
      } else if (item.workItemType === 'Product Backlog Item') {
        stats.pbis++;
        stats.totalItems++;
      } else if (item.workItemType === 'Bug') {
        stats.bugs++;
        stats.totalItems++;
      }

      // Count items with linked tests (coverage is based on items that have linked test cases)
      // Check if item has testedBy field with test IDs, or if it's a Test Case itself
      const hasTests = (item.testedBy && Array.isArray(item.testedBy) && item.testedBy.length > 0) || 
                       (item.workItemType === 'Test Case');
      if (hasTests) {
        stats.itemsWithTests++;
        // Count unique linked tests (avoid duplicates)
        if (item.testedBy && Array.isArray(item.testedBy)) {
          item.testedBy.forEach(testId => {
            teamTestIds[team].add(testId);
          });
        } else if (item.workItemType === 'Test Case') {
          teamTestIds[team].add(item.id);
        }
      }
    });

    // Update linkedTests count with unique test IDs
    Object.keys(teamStats).forEach(team => {
      teamStats[team].linkedTests = teamTestIds[team].size;
    });

    // Calculate coverage and convert to array
    let teams = Object.values(teamStats).map(stats => ({
      ...stats,
      coverage: stats.totalItems > 0 ? Math.round((stats.itemsWithTests / stats.totalItems) * 100) : 0,
      untested: stats.totalItems - stats.itemsWithTests
    }));

    // Sort teams
    if (teamOrderBy) {
      teams = [...teams].sort((a, b) => {
        let aValue, bValue;
        switch (teamOrderBy) {
          case 'team':
            aValue = a.team.toLowerCase();
            bValue = b.team.toLowerCase();
            break;
          case 'features':
            aValue = a.features;
            bValue = b.features;
            break;
          case 'pbis':
            aValue = a.pbis;
            bValue = b.pbis;
            break;
          case 'bugs':
            aValue = a.bugs;
            bValue = b.bugs;
            break;
          case 'linkedTests':
            aValue = a.linkedTests;
            bValue = b.linkedTests;
            break;
          case 'totalItems':
            aValue = a.totalItems;
            bValue = b.totalItems;
            break;
          case 'coverage':
            aValue = a.coverage;
            bValue = b.coverage;
            break;
          case 'untested':
            aValue = a.untested;
            bValue = b.untested;
            break;
          default:
            return 0;
        }
        if (aValue < bValue) return teamOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return teamOrder === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort by team name
      teams = teams.sort((a, b) => a.team.localeCompare(b.team));
    }

    return teams;
  };

  // Handle team table sort request
  const handleTeamSort = (property) => {
    const isAsc = teamOrderBy === property && teamOrder === 'asc';
    setTeamOrder(isAsc ? 'desc' : 'asc');
    setTeamOrderBy(property);
  };

  // Get all test IDs for a specific team
  const getTestIdsForTeam = (teamName) => {
    if (!reportData) return [];

    const allItems = [];
    if (reportData.items) {
      const flattened = flattenTreeForStats(reportData.items);
      allItems.push(...flattened);
    }
    if (reportData.orphanBugs) {
      reportData.orphanBugs.forEach(bug => {
        allItems.push({
          ...bug.item,
          nodeTestCount: bug.testCount || 0
        });
      });
    }

    // Filter items by team
    const teamItems = allItems.filter(item => {
      const itemTeam = item.team && item.team.trim() !== '' ? item.team.trim() : 'Unassigned';
      return itemTeam === teamName;
    });

    // Collect all test IDs
    const testIds = new Set();
    teamItems.forEach(item => {
      if (item.testedBy && Array.isArray(item.testedBy)) {
        item.testedBy.forEach(testId => testIds.add(testId));
      }
    });

    return Array.from(testIds);
  };

  // Fetch test details for a team
  const handleViewTests = async (teamName) => {
    setSelectedTeamName(teamName);
    setLoadingTests(true);
    setTestDialogOpen(true);
    setSelectedTeamTests([]);

    try {
      const testIds = getTestIdsForTeam(teamName);
      
      if (testIds.length === 0) {
        setSelectedTeamTests([]);
        setLoadingTests(false);
        return;
      }

      // Fetch test details in bulk using the /api/tests endpoint with ids filter
      const idsParam = testIds.join(',');
      const response = await get('/api/tests', { ids: idsParam });
      
      if (response && response.success && response.data) {
        setSelectedTeamTests(response.data);
      } else {
        setSelectedTeamTests([]);
      }
    } catch (error) {
      console.error('Error fetching test details:', error);
      setSelectedTeamTests([]);
    } finally {
      setLoadingTests(false);
    }
  };

  // Fetch test details for a specific work item
  const handleViewTestsForItem = async (workItem) => {
    const itemTitle = workItem.title || `Work Item ${workItem.id}`;
    setSelectedTeamName(itemTitle);
    setLoadingTests(true);
    setTestDialogOpen(true);
    setSelectedTeamTests([]);

    try {
      const testIds = workItem.testedBy && Array.isArray(workItem.testedBy) 
        ? workItem.testedBy 
        : [];
      
      if (testIds.length === 0) {
        setSelectedTeamTests([]);
        setLoadingTests(false);
        return;
      }

      // Fetch test details in bulk using the /api/tests endpoint with ids filter
      const idsParam = testIds.join(',');
      const response = await get('/api/tests', { ids: idsParam });
      
      if (response && response.success && response.data) {
        const fetchedTests = response.data;
        setSelectedTeamTests(fetchedTests);
        // Log if there's a discrepancy (some test IDs might not exist in database)
        if (fetchedTests.length !== testIds.length) {
          console.log(`Work Item ${workItem.id}: Found ${fetchedTests.length} of ${testIds.length} test IDs in database`);
        }
      } else {
        setSelectedTeamTests([]);
      }
    } catch (error) {
      console.error('Error fetching test details:', error);
      setSelectedTeamTests([]);
    } finally {
      setLoadingTests(false);
    }
  };

  // Flatten tree for CSV export
  const flattenTree = (nodes, level = 0, parentId = '') => {
    const rows = [];
    nodes.forEach(node => {
      rows.push({
        level,
        workItemId: node.item.id,
        title: node.item.title || '',
        workItemType: node.item.workItemType || '',
        state: node.item.state || '',
        fixVersion: node.item.fixVersion || '',
        createdDate: node.item.createdDate || '',
        team: node.item.team || '',
        parentId: parentId || '',
        testCount: node.testCount || 0,
        prCount: node.prCount || 0
      });
      
      if (node.children && node.children.length > 0) {
        const childRows = flattenTree(node.children, level + 1, node.item.id);
        rows.push(...childRows);
      }
    });
    return rows;
  };

  // Flatten orphan bugs for CSV export
  const flattenOrphanBugs = (nodes) => {
    return nodes.map(node => ({
      level: 0,
      workItemId: node.item.id,
      title: node.item.title || '',
      workItemType: node.item.workItemType || '',
      state: node.item.state || '',
      fixVersion: node.item.fixVersion || '',
      createdDate: node.item.createdDate || '',
      team: node.item.team || '',
      parentId: '',
      testCount: node.testCount || 0,
      prCount: node.prCount || 0
    }));
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!reportData || ((!reportData.items || reportData.items.length === 0) && (!reportData.orphanBugs || reportData.orphanBugs.length === 0))) {
      setError('No data to export');
      return;
    }

    const flattened = reportData.items ? flattenTree(reportData.items) : [];
    const orphanBugsFlattened = reportData.orphanBugs ? flattenOrphanBugs(reportData.orphanBugs) : [];
    
    // Combine all rows
    const allRows = [...flattened, ...orphanBugsFlattened];
    
    // Define CSV headers
    const headers = [
      'Level',
      'Work Item ID',
      'Parent ID',
      'Title',
      'Work Item Type',
      'State',
      'Fix Version',
      'Created Date',
      'Team',
      'Test Count',
      'PR Count',
      'Section'
    ];

    // Convert to CSV rows
    const orphanBugIds = new Set((reportData.orphanBugs || []).map(bug => bug.item.id));
    const csvRows = allRows.map(row => {
      // Determine section (main tree or orphan bugs)
      const section = orphanBugIds.has(row.workItemId) ? 'Orphan Bugs' : 'Main Hierarchy';
      
      const csvRow = [
        row.level,
        row.workItemId,
        row.parentId,
        `"${(row.title || '').replace(/"/g, '""')}"`, // Escape quotes
        row.workItemType,
        row.state,
        row.fixVersion,
        row.createdDate ? formatDate(row.createdDate) : '',
        row.team,
        row.testCount,
        row.prCount,
        section
      ];
      return csvRow.join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `qa-report-${selectedFixVersion}-${new Date().toISOString().split('T')[0]}.csv`;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Get work item type color
  const getWorkItemTypeColor = (workItemType) => {
    switch (workItemType) {
      case 'Epic':
        return 'primary';
      case 'Feature':
        return 'secondary';
      case 'Product Backlog Item':
        return 'success';
      case 'Task':
        return 'info';
      case 'Bug':
        return 'error';
      case 'Test Case':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Render tree node recursively
  const renderTreeNode = (node, level = 0) => {
    const nodeId = node.item.id;
    const isExpanded = expandedNodes.has(nodeId);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <React.Fragment key={nodeId}>
        <TableRow
          hover
          sx={{
            '&:hover': { backgroundColor: 'action.hover' },
            cursor: hasChildren ? 'pointer' : 'default',
            backgroundColor: level > 0 ? 'grey.50' : 'inherit'
          }}
          onClick={() => hasChildren && toggleNode(nodeId)}
        >
          <TableCell sx={{ paddingLeft: `${level * 24 + 8}px` }}>
            <Box display="flex" alignItems="center">
              {hasChildren ? (
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); toggleNode(nodeId); }}
                  sx={{ mr: 0.5 }}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ChevronRightIcon />}
                </IconButton>
              ) : (
                <Box width={32} />
              )}
              <Link 
                href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${nodeId}`} 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ 
                  color: '#1976d2', 
                  textDecoration: 'none',
                  fontWeight: level === 0 ? 600 : 'normal',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {nodeId}
              </Link>
            </Box>
          </TableCell>
          <TableCell>
            {node.item.parent ? (
              <Link 
                href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${node.item.parent}`} 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ color: '#1976d2', textDecoration: 'none' }}
                onClick={(e) => e.stopPropagation()}
              >
                {node.item.parent}
              </Link>
            ) : (
              <Typography variant="body2" color="textSecondary">-</Typography>
            )}
          </TableCell>
          <TableCell>
            <Chip
              label={node.item.workItemType || 'Unknown'}
              size="small"
              color={getWorkItemTypeColor(node.item.workItemType)}
            />
          </TableCell>
          <TableCell>
            <Typography variant="body2">
              {node.item.title || 'Untitled'}
            </Typography>
          </TableCell>
          <TableCell>
            <StatusBadge status={node.item.state} />
          </TableCell>
          <TableCell>{node.item.fixVersion || '-'}</TableCell>
          <TableCell>{node.item.createdDate ? formatDate(node.item.createdDate) : '-'}</TableCell>
          <TableCell align="center">
            {node.testCount > 0 && node.item.testedBy && Array.isArray(node.item.testedBy) && node.item.testedBy.length > 0 ? (
              <Link
                component="button"
                variant="body2"
                onClick={() => handleViewTestsForItem(node.item)}
                sx={{ 
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                <Chip
                  label={node.testCount || 0}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                />
              </Link>
            ) : (
              <Chip
                label={node.testCount || 0}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </TableCell>
          <TableCell align="center">
            <Chip
              label={node.prCount || 0}
              size="small"
              color="secondary"
              variant="outlined"
            />
          </TableCell>
          <TableCell sx={{ 
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {node.item.team || '-'}
          </TableCell>
        </TableRow>
        {hasChildren && isExpanded && node.children.map(child => renderTreeNode(child, level + 1))}
      </React.Fragment>
    );
  };

  if (loadingVersions) {
    return (
      <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading releases...
        </Typography>
      </Paper>
    );
  }

  if (!selectedFixVersion) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a release version to view the QA Report.
        </Alert>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading QA Report...
        </Typography>
      </Paper>
    );
  }

  if (error && !reportData) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!reportData || ((!reportData.items || reportData.items.length === 0) && (!reportData.orphanBugs || reportData.orphanBugs.length === 0))) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Alert severity="info">
          No work items found for release {selectedFixVersion}.
        </Alert>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Release Filter and Actions */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="qa-report-release-select-label">Select Release</InputLabel>
              <Select
                labelId="qa-report-release-select-label"
                id="qa-report-release-select"
                value={selectedFixVersion}
                label="Select Release"
                onChange={(e) => setSelectedFixVersion(e.target.value)}
                disabled={loadingVersions}
              >
                {fixVersions.map((version) => (
                  <MenuItem key={version} value={version}>
                    {version}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel id="qa-report-team-select-label">Team</InputLabel>
              <Select
                labelId="qa-report-team-select-label"
                id="qa-report-team-select"
                multiple
                value={selectedTeams}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedTeams(typeof value === 'string' ? value.split(',') : value);
                }}
                input={<OutlinedInput label="Team" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return 'All Teams';
                  }
                  if (selected.length === 1) {
                    return selected[0];
                  }
                  return `${selected.length} teams selected`;
                }}
                disabled={loading || availableTeams.length === 0}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 300,
                    },
                  },
                }}
              >
                {selectedTeams.length > 0 && (
                  <MenuItem onClick={(e) => { e.stopPropagation(); setSelectedTeams([]); }}>
                    <em>Clear Selection</em>
                  </MenuItem>
                )}
                {availableTeams.length === 0 ? (
                  <MenuItem disabled>
                    <ListItemText primary="No teams found" />
                  </MenuItem>
                ) : (
                  availableTeams.map((team) => (
                    <MenuItem key={team} value={team}>
                      <Checkbox checked={selectedTeams.indexOf(team) > -1} />
                      <ListItemText primary={team} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                QA Report - {selectedFixVersion}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Items: {reportData.totalItems} | Total Tests: {reportData.totalTests} | Total PRs: {reportData.totalPRs}
              </Typography>
            </Box>
            <Tooltip title="Export to CSV">
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            </Tooltip>
          </Box>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Team Summary Table */}
      {(() => {
        const teamStats = calculateTeamStats();
        return teamStats.length > 0 && (
          <TableContainer component={Paper} elevation={2} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={teamOrderBy === 'team'}
                      direction={teamOrderBy === 'team' ? teamOrder : 'asc'}
                      onClick={() => handleTeamSort('team')}
                    >
                      Team
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={teamOrderBy === 'features'}
                      direction={teamOrderBy === 'features' ? teamOrder : 'asc'}
                      onClick={() => handleTeamSort('features')}
                    >
                      Features
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={teamOrderBy === 'pbis'}
                      direction={teamOrderBy === 'pbis' ? teamOrder : 'asc'}
                      onClick={() => handleTeamSort('pbis')}
                    >
                      PBIs
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={teamOrderBy === 'bugs'}
                      direction={teamOrderBy === 'bugs' ? teamOrder : 'asc'}
                      onClick={() => handleTeamSort('bugs')}
                    >
                      Bugs
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={teamOrderBy === 'linkedTests'}
                      direction={teamOrderBy === 'linkedTests' ? teamOrder : 'asc'}
                      onClick={() => handleTeamSort('linkedTests')}
                    >
                      Linked Tests
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={teamOrderBy === 'totalItems'}
                      direction={teamOrderBy === 'totalItems' ? teamOrder : 'asc'}
                      onClick={() => handleTeamSort('totalItems')}
                    >
                      Total Items
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={teamOrderBy === 'coverage'}
                      direction={teamOrderBy === 'coverage' ? teamOrder : 'asc'}
                      onClick={() => handleTeamSort('coverage')}
                    >
                      Coverage
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={teamOrderBy === 'untested'}
                      direction={teamOrderBy === 'untested' ? teamOrder : 'asc'}
                      onClick={() => handleTeamSort('untested')}
                    >
                      Untested
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamStats.map((stats) => (
                  <TableRow key={stats.team} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{stats.team}</TableCell>
                    <TableCell align="right">{stats.features}</TableCell>
                    <TableCell align="right">{stats.pbis}</TableCell>
                    <TableCell align="right">{stats.bugs}</TableCell>
                    <TableCell align="right">
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleViewTests(stats.team)}
                        sx={{ 
                          cursor: 'pointer',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        {stats.linkedTests}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{stats.totalItems}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${stats.coverage}%`}
                        size="small"
                        sx={{
                          backgroundColor: stats.coverage > 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                          color: stats.coverage > 0 ? '#2e7d32' : '#c62828',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          height: '24px'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{stats.untested}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      })()}

      {/* Data Table Section - Collapsible */}
      <Paper elevation={2} sx={{ mb: 2 }}>
        <Box 
          sx={{ 
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' }
          }}
          onClick={() => setDataTableExpanded(!dataTableExpanded)}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Detailed View
          </Typography>
          <IconButton size="small">
            {dataTableExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={dataTableExpanded}>
          {/* Main Hierarchy */}
          {(() => {
            const sortedData = getSortedData();
            return sortedData.items && sortedData.items.length > 0 && (
          <TableContainer component={Paper} elevation={2} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="100px">
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="100px">
                    <TableSortLabel
                      active={orderBy === 'parent'}
                      direction={orderBy === 'parent' ? order : 'asc'}
                      onClick={() => handleRequestSort('parent')}
                    >
                      Parent
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="120px">
                    <TableSortLabel
                      active={orderBy === 'type'}
                      direction={orderBy === 'type' ? order : 'asc'}
                      onClick={() => handleRequestSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="30%">
                    <TableSortLabel
                      active={orderBy === 'title'}
                      direction={orderBy === 'title' ? order : 'asc'}
                      onClick={() => handleRequestSort('title')}
                    >
                      Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="100px">
                    <TableSortLabel
                      active={orderBy === 'state'}
                      direction={orderBy === 'state' ? order : 'asc'}
                      onClick={() => handleRequestSort('state')}
                    >
                      State
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="100px">
                    <TableSortLabel
                      active={orderBy === 'fixVersion'}
                      direction={orderBy === 'fixVersion' ? order : 'asc'}
                      onClick={() => handleRequestSort('fixVersion')}
                    >
                      Fix Version
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="120px">
                    <TableSortLabel
                      active={orderBy === 'createdDate'}
                      direction={orderBy === 'createdDate' ? order : 'asc'}
                      onClick={() => handleRequestSort('createdDate')}
                    >
                      Created Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="100px" align="center">
                    <TableSortLabel
                      active={orderBy === 'testCount'}
                      direction={orderBy === 'testCount' ? order : 'asc'}
                      onClick={() => handleRequestSort('testCount')}
                    >
                      Test Count
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="100px" align="center">
                    <TableSortLabel
                      active={orderBy === 'prCount'}
                      direction={orderBy === 'prCount' ? order : 'asc'}
                      onClick={() => handleRequestSort('prCount')}
                    >
                      PR Count
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width="150px">
                    <TableSortLabel
                      active={orderBy === 'team'}
                      direction={orderBy === 'team' ? order : 'asc'}
                      onClick={() => handleRequestSort('team')}
                    >
                      Team
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.items.map(node => renderTreeNode(node))}
              </TableBody>
            </Table>
          </TableContainer>
            );
          })()}

          {/* Orphan Bugs Section */}
          {(() => {
            const sortedData = getSortedData();
            return sortedData.orphanBugs && sortedData.orphanBugs.length > 0 && (
              <TableContainer component={Paper} elevation={0} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={10}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Orphan Bugs ({sortedData.orphanBugs.length})
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => setOrphanBugsExpanded(!orphanBugsExpanded)}
                      >
                        {orphanBugsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                {orphanBugsExpanded && (
                  <TableRow>
                    <TableCell width="100px">
                      <TableSortLabel
                        active={orderBy === 'id'}
                        direction={orderBy === 'id' ? order : 'asc'}
                        onClick={() => handleRequestSort('id')}
                      >
                        ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="100px">
                      <TableSortLabel
                        active={orderBy === 'parent'}
                        direction={orderBy === 'parent' ? order : 'asc'}
                        onClick={() => handleRequestSort('parent')}
                      >
                        Parent
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="120px">
                      <TableSortLabel
                        active={orderBy === 'type'}
                        direction={orderBy === 'type' ? order : 'asc'}
                        onClick={() => handleRequestSort('type')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="30%">
                      <TableSortLabel
                        active={orderBy === 'title'}
                        direction={orderBy === 'title' ? order : 'asc'}
                        onClick={() => handleRequestSort('title')}
                      >
                        Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="100px">
                      <TableSortLabel
                        active={orderBy === 'state'}
                        direction={orderBy === 'state' ? order : 'asc'}
                        onClick={() => handleRequestSort('state')}
                      >
                        State
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="100px">
                      <TableSortLabel
                        active={orderBy === 'fixVersion'}
                        direction={orderBy === 'fixVersion' ? order : 'asc'}
                        onClick={() => handleRequestSort('fixVersion')}
                      >
                        Fix Version
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="120px">
                      <TableSortLabel
                        active={orderBy === 'createdDate'}
                        direction={orderBy === 'createdDate' ? order : 'asc'}
                        onClick={() => handleRequestSort('createdDate')}
                      >
                        Created Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="100px" align="center">
                      <TableSortLabel
                        active={orderBy === 'testCount'}
                        direction={orderBy === 'testCount' ? order : 'asc'}
                        onClick={() => handleRequestSort('testCount')}
                      >
                        Test Count
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="100px" align="center">
                      <TableSortLabel
                        active={orderBy === 'prCount'}
                        direction={orderBy === 'prCount' ? order : 'asc'}
                        onClick={() => handleRequestSort('prCount')}
                      >
                        PR Count
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width="150px">
                      <TableSortLabel
                        active={orderBy === 'team'}
                        direction={orderBy === 'team' ? order : 'asc'}
                        onClick={() => handleRequestSort('team')}
                      >
                        Team
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                )}
              </TableHead>
              {orphanBugsExpanded && (
                <TableBody>
                  {sortedData.orphanBugs.map(node => {
                    const nodeId = node.item.id;
                    return (
                      <TableRow
                        key={nodeId}
                        hover
                        sx={{
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell>
                          <Link 
                            href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${nodeId}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            sx={{ color: '#1976d2', textDecoration: 'none' }}
                          >
                            {nodeId}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={node.item.workItemType || 'Unknown'}
                            size="small"
                            color="error"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {node.item.title || 'Untitled'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={node.item.state} />
                        </TableCell>
                        <TableCell>{node.item.fixVersion || '-'}</TableCell>
                        <TableCell>{node.item.createdDate ? formatDate(node.item.createdDate) : '-'}</TableCell>
                        <TableCell align="center">
                          {node.testCount > 0 && node.item.testedBy && Array.isArray(node.item.testedBy) && node.item.testedBy.length > 0 ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => handleViewTestsForItem(node.item)}
                              sx={{ 
                                cursor: 'pointer',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              <Chip
                                label={node.testCount || 0}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ cursor: 'pointer' }}
                              />
                            </Link>
                          ) : (
                            <Chip
                              label={node.testCount || 0}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={node.prCount || 0}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {node.item.team || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
        </TableContainer>
            );
          })()}
        </Collapse>
      </Paper>

      {/* Test Details Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Linked Tests for {selectedTeamName} ({selectedTeamTests.length} found)
        </DialogTitle>
        <DialogContent>
          {loadingTests ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading test details...
              </Typography>
            </Box>
          ) : selectedTeamTests.length === 0 ? (
            <Typography variant="body2" align="center" sx={{ py: 4 }}>
              No tests found for this team.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Test ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Assignee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedTeamTests.map((test) => (
                    <TableRow key={test.id || test._id} hover>
                      <TableCell>{test.id || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {test.title || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{test.testType || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={test.testStatus || 'Not Run'}
                          size="small"
                          color={
                            test.testStatus === 'Pass' ? 'success' :
                            test.testStatus === 'Fail' ? 'error' :
                            test.testStatus === 'Blocked' ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={test.state || 'Design'} />
                      </TableCell>
                      <TableCell>{test.priority || '-'}</TableCell>
                      <TableCell>{test.assignee || test.assignedTo || '-'}</TableCell>
                      <TableCell>{test.createdDate ? formatDate(test.createdDate) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QAReportTab;
